import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete, Edit, Quiz } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import packageService, { TestPackage } from '../services/packageService';

const GRADES = ['Class-01', 'Class-02', 'Class-03', 'Class-04', 'Class-05',
  'Class-06', 'Class-07', 'Class-08', 'Class-09', 'Class-10'];
const SUBJECTS = ['Math', 'Science', 'English'];
const SERIES = ['Mock Test - 01', 'Mock Test - 02', 'Mock Test - 03', 'LiveOlympiad Final'];

const first = (values?: string[]) => (values && values.length ? values[0] : '');

/** yyyy-MM-dd for a date input, or '' when unset. */
const toDateInput = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/** Opening dates start the day; closing dates run to the last moment of it. */
const fromDateInput = (value: string, endOfDay: boolean) =>
  value ? new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}Z`).toISOString() : undefined;

const PackageListPage: React.FC = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<TestPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({ grade: '', subject: '', series: '' });

  const [editing, setEditing] = useState<TestPackage | null>(null);
  const [form, setForm] = useState({ name: '', duration: '', openingDate: '', closingDate: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPackages(await packageService.getPackages(filters));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tests');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (pack: TestPackage) => {
    setEditing(pack);
    setForm({
      name: pack.name || '',
      duration: pack.duration != null ? String(pack.duration) : '',
      openingDate: toDateInput(pack.openingDate),
      closingDate: toDateInput(pack.closingDate),
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await packageService.updatePackage(editing.id, {
        name: form.name,
        duration: form.duration ? parseInt(form.duration, 10) : undefined,
        openingDate: fromDateInput(form.openingDate, false),
        closingDate: fromDateInput(form.closingDate, true),
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pack: TestPackage) => {
    if (!window.confirm(`Delete "${pack.name}"? Its questions stay in the item bank.`)) return;
    try {
      await packageService.deletePackage(pack.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Tests
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each test is one grade, subject and series. Questions come from the item bank.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          select size="small" label="Grade" sx={{ minWidth: 150 }}
          value={filters.grade} onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
        >
          <MenuItem value="">All grades</MenuItem>
          {GRADES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Subject" sx={{ minWidth: 150 }}
          value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
        >
          <MenuItem value="">All subjects</MenuItem>
          {SUBJECTS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Series" sx={{ minWidth: 200 }}
          value={filters.series} onChange={(e) => setFilters({ ...filters, series: e.target.value })}
        >
          <MenuItem value="">All series</MenuItem>
          {SERIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {packages.length} {packages.length === 1 ? 'test' : 'tests'}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Test', 'Grade', 'Subject', 'Series', 'Questions', 'Duration', 'Window', ''].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.map((pack) => (
                  <TableRow key={pack.id} hover>
                    <TableCell>{pack.name}</TableCell>
                    <TableCell>{first(pack.grade)}</TableCell>
                    <TableCell>{first(pack.subject)}</TableCell>
                    <TableCell>{first(pack.series)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={pack.questions?.length ?? 0} />
                    </TableCell>
                    <TableCell>{pack.duration ? `${pack.duration} min` : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {pack.openingDate && pack.closingDate
                        ? `${toDateInput(pack.openingDate)} → ${toDateInput(pack.closingDate)}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" title="Questions" onClick={() => navigate(`/tests/${pack.id}`)}>
                        <Quiz fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Edit" onClick={() => openEdit(pack)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Delete" color="error" onClick={() => handleDelete(pack)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {packages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No tests match these filters
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit test</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Name" sx={{ mt: 1, mb: 2 }}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            fullWidth label="Duration (minutes)" sx={{ mb: 2 }}
            value={form.duration}
            onChange={(e) => setForm({ ...form, duration: e.target.value.replace(/[^0-9]/g, '') })}
          />
          <TextField
            fullWidth type="date" label="Opens" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }}
            value={form.openingDate} onChange={(e) => setForm({ ...form, openingDate: e.target.value })}
          />
          <TextField
            fullWidth type="date" label="Closes" InputLabelProps={{ shrink: true }}
            value={form.closingDate} onChange={(e) => setForm({ ...form, closingDate: e.target.value })}
            helperText="Closing runs to the end of the day"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackageListPage;
